// ============================================================================
// JARVISTRAVEL - RECEIPT CARD
// Version: 1.0.0
// Read-only catalog card for a captured receipt in the Journal story. Shows the
// merchant, spend and when — and, when the receipt is matched + confirmed, a
// presence check tying it to an itinerary item. Tokens + lucide, no emoji.
// ============================================================================

import { Receipt, Check, MapPin } from 'lucide-react';
import { formatCurrency } from '@/app/utils';
import type { ReceiptEvidence } from '@/app/api/receipts';

interface ReceiptCardProps {
  receipt: ReceiptEvidence;
  /** Resolved itinerary item title when the receipt is matched to one. */
  matchedLabel?: string;
  /** When budget sync is on: true = matches a card transaction, false = cash. */
  reconciled?: boolean;
}

export function ReceiptCard({ receipt, matchedLabel, reconciled }: ReceiptCardProps) {
  const confirmed = receipt.user_confirmed && Boolean(receipt.matched_item_id);
  const when = new Date(receipt.date_time).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="flex items-center gap-3 rounded-xl bg-surface-raised px-3 py-2.5">
      <div className="w-9 h-9 shrink-0 rounded-lg bg-surface flex items-center justify-center text-muted">
        <Receipt className="w-[18px] h-[18px]" strokeWidth={1.75} aria-hidden="true" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-ink text-sm truncate">{receipt.merchant}</p>
          {confirmed && (
            <span className="inline-flex items-center gap-0.5 shrink-0 text-success text-[11px] font-medium">
              <Check className="w-3 h-3" strokeWidth={2.5} aria-hidden="true" /> Verified
            </span>
          )}
        </div>
        <p className="flex items-center gap-1 text-xs text-muted truncate">
          <span className="shrink-0">{when}</span>
          {matchedLabel ? (
            <span className="truncate">· {matchedLabel}</span>
          ) : receipt.location_hint ? (
            <span className="flex items-center gap-0.5 min-w-0 truncate">
              · <MapPin className="w-3 h-3 shrink-0" strokeWidth={2} aria-hidden="true" />
              <span className="truncate">{receipt.location_hint}</span>
            </span>
          ) : null}
        </p>
      </div>

      <div className="shrink-0 text-right">
        <p className="font-semibold text-ink text-sm tabular-nums">
          {formatCurrency(receipt.amount, receipt.currency)}
        </p>
        {reconciled !== undefined && (
          <span className={`text-[10px] font-medium ${reconciled ? 'text-success' : 'text-muted'}`}>
            {reconciled ? 'On card' : 'Cash'}
          </span>
        )}
      </div>
    </div>
  );
}

export default ReceiptCard;
